import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMap,
  LayersControl,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import Select from "react-select";
import { getDataSetNamePBI, useHttpClient } from "../modules/services/Bundle";
import { RequestMethod } from "../modules/services/core/_enums";
import { IGatewaysModel } from "../modules/services/core/_models";
import { CSSObject } from "@emotion/react";
import { useAuth } from "../modules/auth";

interface Organization {
  OrganizationId: number;
  OrganizationName: string;
  Idcorporacao: number;
  CorporationName: string;
}

interface Corporation {
  id: number;
  name: string;
}

const osmLayer = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
const satelliteLayer =
  "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";
const darkLayer =
  "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png";

// Correção dos ícones padrão do Leaflet
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.9.3/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.3/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.3/dist/images/marker-shadow.png",
});

const FitBounds = ({ bounds }: { bounds: L.LatLngBoundsExpression }) => {
  const map = useMap();
  useEffect(() => {
    if (bounds) {
      map.fitBounds(bounds);
    }
  }, [bounds, map]);
  return null;
};

const GatewayMapPage: React.FC = () => {
  const location = useLocation();
  const httpClient = useHttpClient();
  const { auth } = useAuth();
  const user = auth?.User;
  const emailUsuario = user?.Email;
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const [listaGateway, setListaGateway] = useState<IGatewaysModel[]>([]);
  const [filteredData, setFilteredData] = useState<IGatewaysModel[]>([]);
  const [selectedOrganizations, setSelectedOrganizations] = useState<string[]>([]);
  const [initialOrganizations, setInitialOrganizations] = useState<string[]>([]);
  const [availableOrganizations, setAvailableOrganizations] = useState<Organization[]>([]);
  const [availableCorporations, setAvailableCorporations] = useState<Corporation[]>([]);
  const [selectedCorporation, setSelectedCorporation] = useState<number | null>(null);
  const [userId, setUserId] = useState<string>("");
  const [selectedGateway, setSelectedGateway] = useState<string[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const mapRef = useRef<L.Map>(null);

  const dataSetNamePBI = getDataSetNamePBI();

  // Função para buscar o ID do usuário pelo email
  const fetchUserId = async () => {
    if (!emailUsuario) {
      console.log("Email do usuário não disponível");
      return;
    }

    try {
      const url = `${backendUrl}/api/User/ByEmail/${encodeURIComponent(emailUsuario)}`;
      const response = await fetch(url);

      if (response.ok) {
        const userId = await response.json();
        setUserId(userId.toString());
        return userId.toString();
      }
    } catch (error) {
      console.error("Erro ao buscar ID do usuário:", error);
    }
  };

  // Função para buscar organizações do usuário
  const fetchUserOrganizations = async (id: string) => {
    if (!id) {
      console.log("ID do usuário não disponível");
      return;
    }

    try {
      const url = `${backendUrl}/api/UserOrganization/CorpByUser?userId=${id}`;
      const response = await fetch(url);

      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data) && data.length > 0) {
          const corporationsMap = new Map();

          data.forEach(org => {
            const key = `${org.Idcorporacao}-${org.CorporationName}`;
            if (!corporationsMap.has(key)) {
              corporationsMap.set(key, {
                id: org.Idcorporacao,
                name: org.CorporationName
              });
            }
          });

          const corporations = Array.from(corporationsMap.values());
          setAvailableCorporations(corporations);
          setAvailableOrganizations(data);

          const orgNames = data.map((org: Organization) => org.OrganizationName);
          setInitialOrganizations(orgNames);

          // Seleciona a primeira organização por padrão
          if (orgNames.length > 0) {
            setSelectedOrganizations([orgNames[0]]);
            await obterListaGateway([orgNames[0]]);
          }
        }
      }
    } catch (error) {
      console.error("Erro ao buscar organizações:", error);
    }
  };

  // Inicialização
  useEffect(() => {
    const initializeData = async () => {
      if (!emailUsuario) return;
      const id = await fetchUserId();
      if (id) {
        await fetchUserOrganizations(id);
      }
    };

    initializeData();
  }, [emailUsuario]);

  const obterListaGateway = async (orgs?: string[]) => {
    try {
      const organizacoesPermitidas = orgs || initialOrganizations;

      if (!organizacoesPermitidas || organizacoesPermitidas.length === 0) {
        console.log("Nenhuma organização permitida disponível");
        setListaGateway([]);
        setFilteredData([]);
        return;
      }

      const endpoint = "/Gerenciamento/PowerBI/ObterGateways";
      const queryParams = new URLSearchParams();

      if (dataSetNamePBI) {
        queryParams.append("datasetName", dataSetNamePBI);
      }

      organizacoesPermitidas.forEach((org) => {
        queryParams.append("org", org);
      });

      const queryString = queryParams.toString();
      const fullUrl = `${endpoint}?${queryString}`;

      const response = await httpClient.request({
        method: RequestMethod.GET,
        endpoint: fullUrl,
      });

      if (response.success && response.payload) {
        const validData = response.payload.filter(
          (gateway: IGatewaysModel) =>
            gateway.Latitude !== null &&
            gateway.Longitude !== null &&
            gateway.Latitude !== undefined &&
            gateway.Longitude !== undefined
        );
        setListaGateway(validData);
        setFilteredData(validData);
      }
    } catch (error) {
      console.error("Erro ao obter lista de gateways:", error);
    }
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const gatewayParam = searchParams.get("gateway");
    console.log("Parâmetro 'gateway' da URL:", gatewayParam);

    if (gatewayParam) {
      setSelectedGateway([gatewayParam]); // Configura o gateway selecionado com o parâmetro
    }
  }, [location]);

  useEffect(() => {
    if (listaGateway.length > 0 && selectedGateway.length > 0) {
      console.log(
        "Aplicando filtro inicial com gateways da URL:",
        selectedGateway
      );

      const filtered = listaGateway.filter((gateway) =>
        selectedGateway.includes(gateway.Gateway)
      );

      console.log("Dados filtrados após carregar os gateways:", filtered);
      setFilteredData(filtered);

      // Ajustar a visão do mapa com base nos dados filtrados
      if (mapRef.current && filtered.length > 0) {
        const validCoordinates = filtered.filter(
          (g) => g.Latitude !== null && g.Longitude !== null
        );

        if (validCoordinates.length > 0) {
          const newBounds = L.latLngBounds(
            validCoordinates.map((g) => [g.Latitude!, g.Longitude!])
          );
          console.log("Novos limites do mapa:", newBounds);
          mapRef.current.fitBounds(newBounds);
        }
      }
    }
  }, [listaGateway, selectedGateway]);

  useEffect(() => {
    if (
      selectedGateway.length > 0 &&
      filteredData.length > 0 &&
      mapRef.current
    ) {
      const newBounds = L.latLngBounds(
        filteredData
          .filter((g) => g.Latitude !== null && g.Longitude !== null)
          .map((g) => [g.Latitude!, g.Longitude!])
      );
      mapRef.current.fitBounds(newBounds);
    }
  }, [selectedGateway, filteredData]);

  const handleFilterChange = useCallback(
    (filterType: string, selectedOptions: any) => {
      const selectedValues = selectedOptions
        ? selectedOptions.map((opt: any) => opt.value)
        : [];

      console.log(`Filtro aplicado (${filterType}):`, selectedValues);

      // Criação de uma cópia dos dados originais para filtragem
      let filtered = [...listaGateway];

      // Aplicar filtros com base no tipo
      const applyFilter = (
        filterField: keyof IGatewaysModel,
        values: string[]
      ) => {
        if (values.length > 0) {
          filtered = filtered.filter((gateway) =>
            values.includes(gateway[filterField] as string)
          );
        }
      };

      if (filterType === "organization") {
        setSelectedOrganizations(selectedValues);
        applyFilter("Organizacao", selectedValues);
      }

      if (filterType === "gateway") {
        setSelectedGateway(selectedValues);
        applyFilter("Gateway", selectedValues);
      }

      if (filterType === "status") {
        setSelectedStatus(selectedValues);
        applyFilter("Status", selectedValues);
      }

      console.log("Dados filtrados:", filtered);

      // Atualizar os dados filtrados
      setFilteredData(filtered);

      // Ajustar a visão do mapa
      if (mapRef.current && filtered.length > 0) {
        const validCoordinates = filtered.filter(
          (g) => g.Latitude !== null && g.Longitude !== null
        );

        if (validCoordinates.length > 0) {
          const newBounds = L.latLngBounds(
            validCoordinates.map((g) => [g.Latitude!, g.Longitude!])
          );
          console.log("Novos limites do mapa:", newBounds);
          mapRef.current.fitBounds(newBounds);
        }
      }
    },
    [listaGateway, mapRef]
  );

  const bounds =
    filteredData.length > 0
      ? L.latLngBounds(
        filteredData
          .filter((g) => g.Latitude !== null && g.Longitude !== null)
          .map((g) => [g.Latitude!, g.Longitude!])
      )
      : null;

  const organizationOptions = Array.from(
    new Set(listaGateway.map((g) => g.Organizacao))
  ).map((org) => ({ label: org, value: org }));

  const gatewayOptions = Array.from(
    new Set(listaGateway.map((g) => g.Gateway))
  ).map((gw) => ({ label: gw, value: gw }));

  const statusOptions = Array.from(
    new Set(listaGateway.map((g) => g.Status))
  ).map((st) => ({ label: st, value: st }));

  return (
    <main style={{ height: "100vh", width: "80%" }}>
      <h1>Localização Gateway</h1>
      <div
        style={{
          marginBottom: "10px",
          display: "flex",
          alignItems: "center",
          gap: "10px",
          padding: "10px",
          zIndex: 10000,
        }}
      >
        {/* Filtro de Corporações */}
        <div style={{ flex: 1, maxWidth: "300px" }}>
          <Select
            options={availableCorporations.map((corp) => ({
              label: corp.name,
              value: corp.id,
            }))}
            onChange={(selectedOption: any) => {
              const corpId = selectedOption ? selectedOption.value : null;
              setSelectedCorporation(corpId);

              if (corpId) {
                const filteredOrgs = availableOrganizations
                  .filter(org => org.Idcorporacao === corpId)
                  .map(org => org.OrganizationName);
                setSelectedOrganizations(filteredOrgs);
                obterListaGateway(filteredOrgs);
              } else {
                setSelectedOrganizations([]);
                obterListaGateway([]);
              }
            }}
            placeholder="Filtrar por Corporação"
            isClearable
            classNamePrefix="react-select"
          />
        </div>

        {/* Filtro de Organizações */}
        <div style={{ flex: 1, maxWidth: "300px" }}>
          <Select
            isMulti
            options={availableOrganizations
              .filter(org => !selectedCorporation || org.Idcorporacao === selectedCorporation)
              .map((org) => ({
                label: org.OrganizationName,
                value: org.OrganizationName,
              }))}
            onChange={(selectedOptions: any) => {
              const selectedOrgs = selectedOptions
                ? selectedOptions.map((opt: any) => opt.value)
                : [];
              setSelectedOrganizations(selectedOrgs);
              obterListaGateway(selectedOrgs);
            }}
            value={selectedOrganizations.map(org => ({
              label: org,
              value: org,
            }))}
            placeholder="Filtrar por Organização"
            isClearable
            classNamePrefix="react-select"
          />
        </div>

        {/* Filtro de Gateway */}
        <div style={{ flex: 1, maxWidth: "300px" }}>
          <Select
            isMulti
            options={gatewayOptions}
            onChange={(selected: any) => handleFilterChange("gateway", selected)}
            value={gatewayOptions.filter((option) =>
              selectedGateway.includes(option.value)
            )}
            placeholder="Filtrar por Gateway"
            isClearable
            classNamePrefix="react-select"
          />
        </div>

        {/* Filtro de Status */}
        <div style={{ flex: 1, maxWidth: "300px" }}>
          <Select
            isMulti
            options={statusOptions}
            onChange={(selected: any) => handleFilterChange("status", selected)}
            placeholder="Filtrar por Status"
            isClearable
            classNamePrefix="react-select"
          />
        </div>
      </div>

      <section style={{ marginTop: "20px" }}>
        <h2
          style={{
            textAlign: "center",
            margin: "10px 0",
            color: "goldenrod",
            zIndex: 1,
          }}
        >
          Mapa de gateways
        </h2>
        <MapContainer
          center={[-22.0, -47.0]}
          zoom={6}
          style={{ height: "80vh", width: "100%" }}
          ref={mapRef}
        >
          <LayersControl position="topright">
            <LayersControl.BaseLayer checked name="OpenStreetMap">
              <TileLayer
                url={osmLayer}
                attribution="&copy; OpenStreetMap contributors"
              />
            </LayersControl.BaseLayer>

            <LayersControl.BaseLayer name="Satélite Esri">
              <TileLayer url={satelliteLayer} attribution="Tiles © Esri" />
            </LayersControl.BaseLayer>

            <LayersControl.BaseLayer name="Mapa Escuro">
              <TileLayer url={darkLayer} attribution="&copy; CartoDB" />
            </LayersControl.BaseLayer>
          </LayersControl>

          {filteredData
            .filter(
              (gateway) =>
                gateway.Latitude !== null && gateway.Longitude !== null
            )
            .map((gateway, index) => {
              const iconUrl =
                gateway.PlaceType === "Movel"
                  ? gateway.Status === "online"
                    ? "https://portal.taggen.net/assets/imagens/green-car-48px.png"
                    : "https://portal.taggen.net/assets/imagens/red-car-48px.png"
                  : gateway.Status === "online"
                    ? "https://portal.taggen.net/assets/imagens/green-building-48px.png"
                    : "https://portal.taggen.net/assets/imagens/red-building-48px.png";

              return (
                <Marker
                  key={index}
                  position={[gateway.Latitude!, gateway.Longitude!]}
                  icon={L.icon({
                    iconUrl,
                    iconSize: [32, 32],
                    iconAnchor: [16, 32],
                    popupAnchor: [0, -32],
                  })}
                >
                  <Popup>
                    <strong>Organização:</strong> {gateway.Organizacao}
                    <br />
                    <strong>Local:</strong> {gateway.Local}
                    <br />
                    <strong>Descrição:</strong> {gateway.LocalSituacao}
                    <br />
                    <strong>Gateway:</strong> {gateway.Gateway}
                    <br />
                    <strong>Status:</strong>{" "}
                    {gateway.Status === "online" ? "🟢 online" : "🔴 offline"}
                    <br />
                    <strong>Última Atualização:</strong>{" "}
                    {gateway.UltimaAtualizacao}
                  </Popup>
                </Marker>
              );
            })}
        </MapContainer>
      </section>
    </main>
  );
};

export default GatewayMapPage;
